import { createProject, deleteProject, listProjectSummaries, updateProject, type CoworkProjectSummary } from "@/cowork/api"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { collectionPath, useCollectionScope } from "@/cowork/scope-url"
import { useLanguage } from "@/context/language"
import { dateFormatter } from "@/utils/date"
import { SetupBreadcrumbs } from "@/components/setup-navigation"
import { trialErrorKey } from "@/cowork/trial-errors"
import { DialogCoworkDeleteProject } from "@/pages/home/cowork-project-dialogs"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { useSettingsNavigate } from "@/cowork/settings-navigation"
import { createEffect, createMemo, createResource, For, on, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"

export function CoworkProjectsPage() {
  const scope = useCollectionScope()
  const context = useCoworkWorkspace()
  const language = useLanguage()
  createEffect(() => {
    const companyID = scope().companyID
    if (companyID && context.companies()?.some((company) => company.id === companyID)) context.selectEntity(companyID)
  })
  return <Show when={!scope().companyID || context.state.entityId === scope().companyID}
    fallback={<p role="status" class="p-7">{language.t("cowork.enterprise.scope.selectCompany")}</p>}>
    <CoworkProjectsView entityID={scope().companyID || "ALL"} />
  </Show>
}

export function CoworkProjectsView(props: { entityID: string; embedded?: boolean; onChanged?: () => void }) {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const navigate = useSettingsNavigate()
  const dialog = useDialog()
  const [projects, projectActions] = createResource(() => props.entityID, listProjectSummaries)
  let disposed = false
  onCleanup(() => {
    disposed = true
  })
  const isCurrent = (entityID: string) =>
    !disposed && props.entityID === entityID && (entityID === context.allEntitiesID || context.state.entityId === entityID)
  const switching = () =>
    !props.entityID || (props.entityID !== context.allEntitiesID && context.state.entityId !== props.entityID)
  const [state, setState] = createStore({
    search: "",
    formOpen: false,
    editingID: "",
    name: "",
    saving: false,
    deleting: "",
    error: "",
  })
  createEffect(
    on(
      () => props.entityID,
      () => setState({ formOpen: false, editingID: "", name: "", saving: false, deleting: "", error: "" }),
      { defer: true },
    ),
  )
  const filtered = createMemo(() => {
    if (projects.state !== "ready" && projects.state !== "refreshing") return []
    const query = state.search.trim().toLowerCase()
    return (projects.latest ?? []).filter((project) => {
      if (props.entityID !== context.allEntitiesID && project.company_id !== props.entityID) return false
      if (!query) return true
      return `${project.name} ${project.company_name} ${project.description ?? ""}`.toLowerCase().includes(query)
    })
  })

  const openProject = (project: CoworkProjectSummary) => {
    if (disposed || (props.entityID !== context.allEntitiesID && project.company_id !== props.entityID)) return
    context.selectEntity(project.company_id)
    context.selectProject(project.id)
    navigate(collectionPath("periods", { companyID: project.company_id, projectID: project.id }))
  }

  const editProject = (project: CoworkProjectSummary) => {
    if (
      disposed ||
      switching() ||
      state.saving ||
      (project.project_role !== "owner" && project.project_role !== "editor")
    ) return
    if (props.entityID !== context.allEntitiesID && project.company_id !== props.entityID) return
    context.selectEntity(project.company_id)
    setState({ formOpen: true, editingID: project.id, name: project.name, error: "" })
  }

  const resetForm = () => setState({ formOpen: false, editingID: "", name: "", error: "" })

  const saveProject = async (event: SubmitEvent) => {
    event.preventDefault()
    if (!state.name.trim() || state.saving || switching() || disposed) return
    const entityID = props.entityID
    const editingID = state.editingID
    const name = state.name.trim()
    let companyID = entityID !== context.allEntitiesID ? entityID : context.state.entityId
    if (editingID) {
      const project = projects()?.find((project) => project.id === editingID)
      if (!project || (project.project_role !== "owner" && project.project_role !== "editor")) return
      if (entityID !== context.allEntitiesID && project.company_id !== entityID) return
      companyID = project.company_id
      context.selectEntity(project.company_id)
    } else if (entityID !== context.allEntitiesID) {
      context.selectEntity(entityID)
    }
    setState({ saving: true, error: "" })
    try {
      const project = editingID ? await updateProject(editingID, name, companyID) : await createProject(name, companyID)
      if (!isCurrent(entityID)) return
      props.onChanged?.()
      await Promise.all([projectActions.refetch(), context.refresh()])
      if (!isCurrent(entityID)) return
      resetForm()
      if (!editingID) {
        context.selectEntity(project.company_id)
        context.selectProject(project.id)
        navigate(collectionPath("periods", { companyID: project.company_id, projectID: project.id }))
      }
    } catch (error) {
      const key = trialErrorKey(error)
      if (isCurrent(entityID)) setState("error", key ? language.t(key) : error instanceof Error ? error.message : String(error))
    } finally {
      if (isCurrent(entityID)) setState("saving", false)
    }
  }

  const removeProject = async (project: CoworkProjectSummary) => {
    if (disposed || switching() || state.deleting || project.project_role !== "owner") return
    const entityID = props.entityID
    if (entityID !== context.allEntitiesID && project.company_id !== entityID) return
    if (entityID !== context.allEntitiesID) context.selectEntity(entityID)
    setState({ deleting: project.id, error: "" })
    try {
      await deleteProject(project.id, project.company_id)
      if (!isCurrent(entityID)) return
      props.onChanged?.()
      if (context.state.projectId === project.id) context.selectProject("")
      await Promise.all([projectActions.refetch(), context.refresh()])
    } catch (error) {
      if (isCurrent(entityID)) setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      if (isCurrent(entityID)) setState("deleting", "")
    }
  }

  return (
    <div
      class={props.embedded ? "min-w-0 w-full" : "min-h-full min-w-0 bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10"}
    >
      <div class={props.embedded ? "min-w-0" : "mx-auto min-w-0 max-w-[1500px]"}>
        <Show when={!props.embedded}>
          <SetupBreadcrumbs items={[
            { label: language.t("cowork.enterprise.nav.settings"), href: "/settings" },
            { label: language.t("cowork.setup.title"), href: "/settings/setup" },
            { label: language.t("cowork.projects.title") },
          ]} />
        </Show>
        <header class="flex min-w-0 flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <Show when={!props.embedded}>
            <div>
              <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.projects.title")}</h1>
              <p class="mt-1 text-[13px] text-v2-text-text-muted">
                {language.t("cowork.enterprise.projects.description")}
              </p>
            </div>
          </Show>
          <div class="flex w-full min-w-0 gap-2 sm:w-auto sm:max-w-full">
            <label class="flex h-10 min-w-0 flex-1 items-center rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 sm:w-[260px]">
              <input
                class="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-v2-text-text-faint"
                aria-label={language.t("cowork.enterprise.projects.searchLabel")}
                placeholder={language.t("cowork.enterprise.projects.search")}
                value={state.search}
                onInput={(event) => setState("search", event.currentTarget.value)}
              />
            </label>
            <button
              type="button"
              class="h-10 shrink-0 whitespace-nowrap rounded-lg bg-v2-background-bg-contrast px-4 py-2 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
              disabled={switching() || state.saving || props.entityID === context.allEntitiesID}
              title={props.entityID === context.allEntitiesID ? language.t("cowork.enterprise.scope.selectCompany") : undefined}
              onClick={() => {
                resetForm()
                setState("formOpen", true)
              }}
            >
              {language.t("cowork.enterprise.projects.add")}
            </button>
          </div>
        </header>
        <div class="mt-5 flex flex-wrap items-end gap-4 rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-4">
          <label class="grid min-w-56 gap-2 text-[12px]">
            {language.t("cowork.enterprise.scope.entity")}
            <select class="h-10 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3"
              value={props.entityID} disabled={state.saving || !!state.deleting}
              onChange={(event) => {
                const companyID = event.currentTarget.value
                if (companyID !== context.allEntitiesID) context.selectEntity(companyID)
                navigate(collectionPath("projects", { companyID: companyID === context.allEntitiesID ? undefined : companyID }))
              }}>
              <option value={context.allEntitiesID}>{language.t("cowork.enterprise.scope.allCompanies")}</option>
              <For each={context.companies()}>{(company) => <option value={company.id}>{company.name}</option>}</For>
            </select>
          </label>
          <p class="pb-2 text-[12px] text-v2-text-text-muted">{language.t("cowork.setup.periodHint")}</p>
        </div>
        <Show when={state.error && !state.formOpen}>
          <div class="mt-4 rounded-lg bg-v2-state-bg-danger px-4 py-3 text-[11px] text-v2-state-fg-danger">
            {state.error}
          </div>
        </Show>

        <Show when={state.formOpen}>
          <form
            class="mt-6 flex flex-col gap-3 rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5 sm:flex-row"
            onSubmit={saveProject}
          >
            <input
              required
              class="h-10 min-w-0 flex-1 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none"
              aria-label={language.t("cowork.project.name")}
              placeholder={language.t("cowork.projects.placeholder")}
              value={state.name}
              onInput={(event) => setState("name", event.currentTarget.value)}
            />
            <div class="flex justify-end gap-2">
              <button type="button" class="rounded-md px-3 py-2 text-[11px]" onClick={resetForm}>
                {language.t("common.cancel")}
              </button>
              <button
                type="submit"
                class="rounded-md bg-v2-background-bg-contrast px-4 py-2 text-[11px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                disabled={state.saving || switching() || !state.name.trim()}
              >
                {state.saving
                  ? language.t("cowork.enterprise.settings.saving")
                  : language.t(state.editingID ? "common.save" : "cowork.enterprise.projects.create")}
              </button>
            </div>
            <Show when={state.error}>
              <p class="text-[11px] text-v2-state-fg-danger sm:basis-full">{state.error}</p>
            </Show>
          </form>
        </Show>

        <section class="mt-7 overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
          <div class="flex items-center justify-between border-b border-v2-border-border-base px-5 py-4">
            <div>
              <h2 class="text-[15px] font-semibold">{language.t("cowork.enterprise.projects.portfolio")}</h2>
              <p class="mt-0.5 text-[11px] text-v2-text-text-muted">
                {language.t("cowork.enterprise.projects.count", { count: filtered().length })}
              </p>
            </div>
          </div>
          <Show when={projects.error}>
            <p role="alert" class="p-5 text-[12px]">{language.t("cowork.enterprise.project.loadError")} <button type="button" class="underline" onClick={() => void projectActions.refetch()}>{language.t("cowork.enterprise.scope.retry")}</button></p>
          </Show>
          <Show
            when={!projects.loading && filtered().length > 0}
            fallback={
              <Show when={!projects.error}>
              <div class="grid min-h-56 place-items-center p-8 text-[12px] text-v2-text-text-muted">
                {projects.loading ? (
                  <span class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
                ) : (
                  language.t("cowork.enterprise.projects.empty")
                )}
              </div>
              </Show>
            }
          >
            <div class="overflow-x-auto">
              <table class="w-full min-w-[620px] border-collapse text-left">
                <thead>
                  <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                    <th class="px-5 py-3">{language.t("cowork.enterprise.projects.project")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.dashboard.scope")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.companies.lastActivity")}</th>
                    <th class="px-5 py-3 text-right">{language.t("cowork.enterprise.employees.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={filtered()}>
                    {(project) => (
                      <tr class="border-b border-v2-border-border-muted last:border-0 hover:bg-v2-overlay-simple-overlay-hover">
                        <td class="px-5 py-4">
                          <button type="button" class="text-left" onClick={() => openProject(project)}>
                            <span class="block text-[13px] font-medium">{project.name}</span>
                            <Show when={project.description}><span class="mt-0.5 block max-w-sm truncate text-[11px] text-v2-text-text-muted">{project.description}</span></Show>
                          </button>
                        </td>
                        <td class="px-4 py-4">
                          <span class="rounded-md bg-v2-background-bg-layer-03 px-2 py-1 text-[10px] font-medium">
                            {project.company_name}
                          </span>
                        </td>
                        <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">
                          {formatDate(project.last_activity || project.updated_at)}
                        </td>
                        <td class="px-5 py-4 text-right">
                          <div class="flex flex-wrap justify-end gap-2">
                            <button type="button" class="rounded-lg border border-v2-border-border-base px-3 py-2 text-[12px] font-medium" onClick={() => openProject(project)}>{language.t("cowork.enterprise.period.title")}</button>
                            <Show when={project.project_role === "owner" || project.project_role === "editor"}>
                              <button
                                type="button"
                                class="rounded-lg border border-v2-border-border-base px-3 py-2 text-[12px] font-medium hover:bg-v2-overlay-simple-overlay-hover disabled:opacity-50"
                                aria-label={language.t("common.edit")}
                                disabled={switching() || state.saving}
                                onClick={() => editProject(project)}
                              >{language.t("common.edit")}</button>
                            </Show>
                            <Show when={project.project_role === "owner"}>
                              <button
                                type="button"
                                class="rounded-lg border border-v2-border-border-base px-3 py-2 text-[12px] font-medium text-v2-state-fg-danger hover:bg-v2-overlay-simple-overlay-hover disabled:opacity-50"
                                aria-label={language.t("common.delete")}
                                disabled={switching() || state.deleting === project.id}
                                onClick={() => {
                                  const entityID = props.entityID
                                  dialog.show(() => (
                                    <DialogCoworkDeleteProject
                                      name={project.name}
                                      onDelete={() => {
                                        if (isCurrent(entityID)) void removeProject(project)
                                      }}
                                    />
                                  ))
                                }}
                              >{language.t("common.delete")}</button>
                            </Show>
                          </div>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </section>
      </div>
    </div>
  )
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime())
    ? dateFormatter(undefined, { day: "numeric", month: "short", year: "numeric" }).format(date)
    : ""
}
